import type { CSSProperties, ReactNode } from "react";
import { EXAMPLE_ENVOY } from "@/lib/example";
import type { PreviewKind } from "@/lib/previews";
import ApplicationsPreview from "./ApplicationsPreview";
import JobLine, { CARD } from "./JobLine";
import PlacePreview from "./PlacePreview";
import ResumePreview from "./ResumePreview";
import SavedPreview from "./SavedPreview";

function Chips({ items }: { items: readonly string[] }) {
    return (
        <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
            {items.map((item) => (
                <li
                    key={item}
                    className="rounded-full border border-line bg-card px-3 py-1 text-[13px] font-semibold"
                >
                    {item}
                </li>
            ))}
        </ul>
    );
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
    return (
        <section className={`${CARD} flex flex-col gap-3 px-[18px] py-4`}>
            <h2 className="m-0 text-[15px] font-semibold">{title}</h2>
            {children}
        </section>
    );
}

/** Envoy: Jordan's envoy, on, with this week's counts, what it looks for, and this morning's matches. */
export function EnvoyPreview() {
    return (
        <PlacePreview kind="envoy" note="Free. Finding matches never spends a tailor.">
            <div className="flex flex-col gap-3">
                <p className="m-0 flex items-center gap-2.5 px-1.5 text-[15px] font-semibold">
                    <span
                        className="envoy-pulse size-2.5 rounded-full bg-green"
                        aria-hidden="true"
                    />
                    Jordan&rsquo;s envoy is looking
                </p>
                <ul className="m-0 grid list-none grid-cols-3 gap-3 p-0 max-[560px]:grid-cols-1">
                    {EXAMPLE_ENVOY.tiles.map((tile, index) => (
                        <li
                            key={tile.label}
                            className={`${CARD} flow-step flex flex-col gap-1 px-[18px] py-4`}
                            style={{ "--step": index } as CSSProperties}
                        >
                            <span className="text-[12.5px] text-fg-muted">{tile.label}</span>
                            <span className="font-mono text-[26px] font-bold">{tile.value}</span>
                        </li>
                    ))}
                </ul>
                <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)] gap-3 max-[860px]:grid-cols-1">
                    <Panel title="It looks for">
                        <Chips items={EXAMPLE_ENVOY.lookingFor} />
                    </Panel>
                    <Panel title="This morning's matches">
                        <ul className="m-0 flex list-none flex-col p-0">
                            {EXAMPLE_ENVOY.matches.map((job) => (
                                <li
                                    key={job.company}
                                    className="border-t border-line-soft py-2.5 first:border-t-0 first:pt-0 last:pb-0"
                                >
                                    <JobLine job={job} />
                                </li>
                            ))}
                        </ul>
                    </Panel>
                </div>
            </div>
        </PlacePreview>
    );
}

/** The preview for a place an account keeps. */
export default function PreviewFor({ kind }: { kind: PreviewKind }) {
    switch (kind) {
        case "resume":
            return <ResumePreview />;
        case "applications":
            return <ApplicationsPreview />;
        case "saved":
            return <SavedPreview />;
        case "envoy":
            return <EnvoyPreview />;
    }
}
